<template>
  <Dialog scrollable :common-dialog="visitDialog" :dialog-width="dialogWidth">
    <template v-slot:title>
      <div
        v-if="visitDialog"
        class="d-flex align-center justify-space-between w-100 px-2 py-1"
      >
        <div class="d-flex align-center flex-wrap gap-2">
          <!-- White square document badge -->
          <div
            class="rounded-lg bg-white pa-2 border-slate d-flex align-center justify-center mr-2 shadow-sm"
          >
            <v-icon color="cyan darken-2" medium
              >mdi-calendar-account-outline</v-icon
            >
          </div>

          <h1
            class="form-title custom-nowrap-ellipsis text-capitalize mb-0 cursor-pointer font-weight-700 color-custom-blue font-size-22 mr-2"
          >
            Leave #{{ visitDetail.barcode }}
          </h1>

          <template v-if="visitDetail.status">
            <v-chip
              class="text-uppercase white--text font-weight-600 px-3"
              color="green darken-1"
              small
              label
            >
              Approved
            </v-chip>
          </template>
        </div>

        <!-- Action Buttons -->
        <div v-if="!pageLoading" class="d-flex align-center gap-2">
          <!-- Close Button -->
          <v-btn
            outlined
            color="grey darken-2"
            class="custom-bold-button bg-white rounded-lg px-4"
            height="36"
            v-on:click="$emit('close', true)"
          >
            <v-icon small left>mdi-close</v-icon>
            CLOSE
          </v-btn>
        </div>
      </div>
    </template>

    <!-- Main Modal Body Content -->
    <template v-slot:body>
      <div class="pa-4">
        <!-- Staff Title Link Banner -->
        <div
          v-if="visitDetail.staff && visitDetail.staff.full_name"
          class="mb-3"
        >
          <span class="font-weight-700 font-size-18 color-custom-blue">
            {{ visitDetail.staff.full_name }}
          </span>
          <span
            v-if="visitDetail.category"
            class="ml-2 font-weight-600 font-size-15 text-muted"
          >
            - {{ visitDetail.category }}
          </span>
        </div>

        <!-- Top Split Card (Left White Half & Right Soft Blue Half) -->
        <v-card
          flat
          outlined
          class="rounded-lg overflow-hidden pl-4 mb-4 border-slate shadow-sm"
        >
          <v-row no-gutters>
            <!-- Left Half: Leave Key Information (White Background) -->
            <v-col cols="12" md="6" class="bg-white pa-4 border-right-slate">
              <div class="d-flex align-center mb-3">
                <v-avatar size="32" color="cyan lighten-5" class="mr-2">
                  <v-icon small color="cyan darken-2"
                    >mdi-account-clock-outline</v-icon
                  >
                </v-avatar>
                <span class="font-weight-700 font-size-15 color-custom-blue"
                  >Leave Details</span
                >
              </div>

              <!-- Leave Info Rows -->
              <div class="d-flex flex-column gap-3">
                <!-- Staff Name -->
                <div class="d-flex align-center">
                  <div class="d-flex align-center" style="width: 150px">
                    <v-avatar size="26" color="cyan lighten-5" class="mr-2">
                      <v-icon x-small color="cyan darken-2">mdi-account</v-icon>
                    </v-avatar>
                    <span class="font-weight-600 font-size-14 text-slate-700"
                      >Staff Name</span
                    >
                  </div>
                  <span class="font-weight-700 font-size-14 color-custom-blue">
                    {{
                      visitDetail.staff ? visitDetail.staff.full_name : "N/A"
                    }}
                  </span>
                </div>

                <!-- Leave Number -->
                <div class="d-flex align-center">
                  <div class="d-flex align-center" style="width: 150px">
                    <v-avatar size="26" color="cyan lighten-5" class="mr-2">
                      <v-icon x-small color="cyan darken-2">mdi-pound</v-icon>
                    </v-avatar>
                    <span class="font-weight-600 font-size-14 text-slate-700"
                      >Leave Number</span
                    >
                  </div>
                  <Barcode dense small :barcode="visitDetail.barcode"></Barcode>
                </div>

                <!-- Leave Type -->
                <div class="d-flex align-center">
                  <div class="d-flex align-center" style="width: 150px">
                    <v-avatar size="26" color="cyan lighten-5" class="mr-2">
                      <v-icon x-small color="cyan darken-2"
                        >mdi-bookmark-outline</v-icon
                      >
                    </v-avatar>
                    <span class="font-weight-600 font-size-14 text-slate-700"
                      >Leave Type</span
                    >
                  </div>
                  <v-chip
                    small
                    color="cyan darken-1"
                    class="white--text font-weight-600 px-3"
                    label
                  >
                    {{ visitDetail.category || "N/A" }}
                  </v-chip>
                </div>

                <!-- Start & End Date -->
                <div class="d-flex align-center">
                  <div class="d-flex align-center" style="width: 150px">
                    <v-avatar size="26" color="cyan lighten-5" class="mr-2">
                      <v-icon x-small color="cyan darken-2"
                        >mdi-calendar-range</v-icon
                      >
                    </v-avatar>
                    <span class="font-weight-600 font-size-14 text-slate-700"
                      >Duration</span
                    >
                  </div>
                  <v-chip
                    small
                    color="cyan lighten-5"
                    class="cyan--text text--darken-3 font-weight-600 px-2"
                    label
                  >
                    {{ formatDate(visitDetail.start_date) }} to
                    {{ formatDate(visitDetail.end_date) }}
                  </v-chip>
                </div>

                <!-- Half Day -->
                <div
                  class="d-flex align-center"
                  v-if="visitDetail && visitDetail.duration"
                >
                  <div class="d-flex align-center" style="width: 150px">
                    <v-avatar size="26" color="cyan lighten-5" class="mr-2">
                      <v-icon x-small color="cyan darken-2"
                        >mdi-clock-outline</v-icon
                      >
                    </v-avatar>
                    <span class="font-weight-600 font-size-14 text-slate-700"
                      >Half Day</span
                    >
                  </div>
                  <v-chip
                    small
                    color="green darken-1"
                    class="white--text font-weight-600 px-2"
                    label
                  >
                    <template v-if="visitDetail.day_type == 1">
                      {{ formatDate(visitDetail.start_date) }}
                    </template>
                    <template v-else>
                      {{ formatDate(visitDetail.end_date) }}
                    </template>
                    {{
                      visitDetail.duration_type == 1
                        ? " (First Half)"
                        : " (Second Half)"
                    }}
                  </v-chip>
                </div>

                <!-- Number of Days -->
                <div class="d-flex align-center">
                  <div class="d-flex align-center" style="width: 150px">
                    <v-avatar size="26" color="cyan lighten-5" class="mr-2">
                      <v-icon x-small color="cyan darken-2">mdi-counter</v-icon>
                    </v-avatar>
                    <span class="font-weight-600 font-size-14 text-slate-700"
                      >Total Days</span
                    >
                  </div>
                  <v-chip
                    small
                    color="green darken-1"
                    class="white--text font-weight-700 px-3"
                    label
                  >
                    {{ visitDetail.total_leave_days }} Days
                  </v-chip>
                </div>

                <!-- Reason -->
                <div class="d-flex align-start">
                  <div class="d-flex align-center" style="width: 150px">
                    <v-avatar size="26" color="cyan lighten-5" class="mr-2">
                      <v-icon x-small color="cyan darken-2"
                        >mdi-text-box-outline</v-icon
                      >
                    </v-avatar>
                    <span class="font-weight-600 font-size-14 text-slate-700"
                      >Reason</span
                    >
                  </div>
                  <span class="font-weight-500 font-size-14 text-slate-700">
                    {{ visitDetail.reason || "No reason provided" }}
                  </span>
                </div>
              </div>
            </v-col>

            <!-- Right Half: Leave Attachments (Soft Light Blue Background) -->
            <v-col cols="12" md="6" class="bg-blue-light pa-4">
              <div class="d-flex align-center mb-3">
                <v-avatar size="32" color="blue lighten-5" class="mr-2">
                  <v-icon small color="primary">mdi-paperclip</v-icon>
                </v-avatar>
                <span class="font-weight-700 font-size-15 color-custom-blue"
                  >Leave Attachments</span
                >
              </div>

              <!-- Attachments Table -->
              <v-simple-table
                fixed-header
                height="24vh"
                class="rounded-lg border-slate"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th width="50" class="simple-table-th">Preview</th>
                      <th class="simple-table-th">File Name</th>
                      <th width="80" class="simple-table-th text-center">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    v-if="
                      visitDetail.attachments && visitDetail.attachments.length
                    "
                  >
                    <tr
                      v-for="(row, index) in visitDetail.attachments"
                      :key="index"
                    >
                      <td class="simple-table-td">
                        <template
                          v-if="row.extension && isImage(row.extension)"
                        >
                          <ImageTemplate
                            :src="row.file.url"
                            style="max-width: 40px; width: 40px"
                            class="rounded"
                          ></ImageTemplate>
                        </template>
                        <template v-else>
                          <inline-svg
                            :src="$assetURL(`media/mime/${row.extension}.svg`)"
                            width="28"
                            height="28"
                          />
                        </template>
                      </td>
                      <td
                        class="simple-table-td font-weight-600 color-custom-blue cursor-pointer"
                        v-on:click="downloadAttachment(row.file.url)"
                      >
                        {{ row.name }}
                      </td>
                      <td class="simple-table-td text-center">
                        <v-btn
                          icon
                          small
                          color="primary"
                          v-on:click="downloadAttachment(row.file.url)"
                        >
                          <v-icon small>mdi-download</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot v-else>
                    <tr>
                      <td colspan="3" class="text-center py-4">
                        <span class="text-muted font-size-14 italic"
                          >No attachments found</span
                        >
                      </td>
                    </tr>
                  </tfoot>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-card>
      </div>
    </template>
  </Dialog>
</template>

<script>
import moment from "moment-timezone";
import Dialog from "@/view/pages/partials/Dialog.vue";
import Barcode from "@/view/pages/partials/Barcode.vue";
import CommonMixin from "@/core/plugins/common-mixin";
import ImageTemplate from "@/view/pages/Image";

//import Priority from "@/view/pages/partials/Priority.vue";
/* import CustomStatus from "@/view/pages/partials/CustomStatus.vue"; */
import {
  /*  PUT,
     QUERY,
     POST,
     PATCH, */
  GET,
} from "@/core/services/store/request.module";
/* import JwtService from "@/core/services/jwt.service";
 import { ErrorEventBus, InitializeError } from "@/core/lib/message.lib";
 import { ServiceFormEventBus } from "@/core/lib/service.form.lib";
 import { toNumber } from "lodash";
 import { downloadEvent } from "@/core/lib/ics.lib";
 import Datepicker from "@/view/pages/partials/Datepicker.vue"; */
//import Timepicker from "@/view/pages/partials/Timepicker.vue";
/* import VueTimepicker from "vue2-timepicker/src/vue-timepicker.vue"; */

moment.tz.setDefault(process.env.VUE_APP_TIMEZONE);

export default {
  mixins: [CommonMixin],
  props: {
    visitDialog: {
      type: Boolean,
      default: false,
    },
    isCalendar: {
      type: Boolean,
      default: false,
    },
    visit: {
      type: Number,
      default: 0,
    },
  },
  watch: {
    visit: {
      deep: true,
      immediate: true,
      handler(param) {
        if (param) {
          this.loadContent();
        }
      },
    },
  },
  data() {
    return {
      visitId: null,
      allowUpdate: false,
      pageLoading: false,
      detail: new Object(),
      visitDetail: {},
      extensionArray: ["jpg", "png", "gif", "jpeg", "webp", "tiff", "jfif"],
    };
  },
  methods: {
    isImage(ext) {
      if (find(this.extensionArray, (row) => row == ext.toLowerCase())) {
        return true;
      } else {
        return false;
      }
    },
    downloadAttachment(url) {
      window.open(url, "_blank");
    },
    loadContent() {
      const _this = this;
      _this.pageLoading = true;
      _this.$store
        .dispatch(GET, {
          url: "leave/" + _this.visit,
        })
        .then(({ data }) => {
          _this.visitDetail = data;
          console.log(_this.visitDetail, "_this.visitDetail");
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          //_this.getActiveTimer();
          _this.pageLoading = false;
        });
    },
  },
  components: {
    Dialog,
    // Priority,
    Barcode,
    /*  Datepicker, */
    // Timepicker,
    /* VueTimepicker, */
    /*  CreateTimeCloser,
         DeleteDialog, */
    /*    CustomStatus, */
    ImageTemplate,
  },
  computed: {
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 70);
    },
    updateStartDate() {
      if (this.lodash.isEmpty(this.visitDetail) === false) {
        return moment(this.visitDetail.started_at).format("YYYY-MM-DD");
      }
      return null;
    },
    updateStartTime() {
      if (this.lodash.isEmpty(this.visitDetail) === false) {
        return moment(this.visitDetail.started_at).format("hh:mm A");
      }
      return null;
    },
    updateEndTime() {
      if (this.lodash.isEmpty(this.visitDetail) === false) {
        return moment(this.visitDetail.finished_at).format("hh:mm A");
      }
      return null;
    },
    canUpdateVisit() {
      if (this.visitDetail && this.visitDetail.status != 1) {
        return false;
      }
      return this.getPermission("visit:update");
    },
    getAppointmentText() {
      if (this.lodash.isEmpty(this.visitDetail) === false) {
        let date = this.formatDate(this.visitDetail.started_at);
        let startTime = moment(this.visitDetail.started_at).format("hh:mm A");
        let endTime = moment(this.visitDetail.finished_at).format("hh:mm A");
        return date + " " + startTime + " - " + endTime;
      }
      return null;
    },
  },
};
</script>

<style scoped>
.border-slate {
  border-color: #e2e8f0 !important;
}
.border-right-slate {
  border-right: 1px solid #e2e8f0 !important;
}
.border-right-dashed-sm {
  border-right: 1px dashed #cbd5e1 !important;
}
@media (max-width: 600px) {
  .border-right-dashed-sm {
    border-right: none !important;
    border-bottom: 1px dashed #cbd5e1 !important;
    padding-bottom: 12px;
    margin-bottom: 12px;
  }
}
.border-dashed {
  border-style: dashed !important;
}
.bg-slate-50 {
  background-color: #f8fafc !important;
}
.bg-blue-light {
  background-color: #eef6fc !important;
}
.hover-cyan:hover {
  color: #00b4d8 !important;
}
.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}
</style>
